import { LogLevel, loggerRef, toJson } from '@amazon/vinyl-util'

const REDACTED = '[redacted]'

const CREDENTIAL_HEADER =
    /^(authorization|proxy-authorization|x-amz-access-token|cookie|set-cookie|x-api-key|.*csrf.*)$/i
const SECRET_FIELD = /^(refresh_token|access_token|id_token|code_verifier|client_secret)$/i

const SECRET_ASSIGNMENT =
    /((?:refresh_token|access_token|id_token|code_verifier|client_secret)["']?\s*[:=]\s*["']?)[^&\s"',}]+/gi
const QUERY_CODE = /([?&]code=)[^&#\s"']*/gi
const BEARER = /\b(Bearer\s+)[^\s"',]+/gi
const AMAZON_TOKEN = /\bAtz[ar]\|[\w\-|.~+/=]+/g

/** Masks tokens and secret query or form fields inside free text, keeping everything else. */
export function redactText(text: string): string {
    return text
        .replace(SECRET_ASSIGNMENT, `$1${REDACTED}`)
        .replace(QUERY_CODE, `$1${REDACTED}`)
        .replace(BEARER, `$1${REDACTED}`)
        .replace(AMAZON_TOKEN, REDACTED)
}

const QUERY_VALUE = /([?&][^=&#\s"'<>]+=)[^&#\s"'<>]*/g
const HEADER_ASSIGNMENT =
    /\b((?:set-)?cookie|(?:proxy-)?authorization|x-amz-access-token|x-api-key)(["']?\s*[:=]\s*["']?)[^\r\n"']*/gi

/**
 * {@link redactText} for text that leaves the device in a bug report: it also masks every URL
 * query value and the value of a cookie or credential header, which log lines may print whole.
 */
export function redactReportText(text: string): string {
    return redactText(text)
        .replace(HEADER_ASSIGNMENT, `$1$2${REDACTED}`)
        .replace(QUERY_VALUE, `$1${REDACTED}`)
}

/** Copies a JSON-like value with {@link redactReportText} applied to every string in it. */
export function redactReportJson<T>(value: T): T {
    if (typeof value === 'string') return redactReportText(value) as T
    if (Array.isArray(value)) return value.map(redactReportJson) as T
    if (typeof value !== 'object' || value === null) return value
    return Object.fromEntries(
        Object.entries(value).map(([key, entry]) => [key, redactReportJson(entry)])
    ) as T
}

/**
 * Copies a JSON-like value with request bodies, credential headers and secret fields masked and
 * secrets in every string replaced. Method, URL, status, request id and timing pass through.
 */
export function redactJson(value: unknown): unknown {
    if (typeof value === 'string') return redactText(value)
    if (Array.isArray(value)) {
        const [name] = value as unknown[]
        if (value.length === 2 && typeof name === 'string' && CREDENTIAL_HEADER.test(name)) {
            return [name, REDACTED]
        }
        return value.map(redactJson)
    }
    if (typeof value !== 'object' || value === null) return value
    const out: Record<string, unknown> = {}
    for (const [key, entry] of Object.entries(value)) {
        out[key] =
            key === 'body' || CREDENTIAL_HEADER.test(key) || SECRET_FIELD.test(key)
                ? REDACTED
                : redactJson(entry)
    }
    return out
}

const LOG_REDACTING = Symbol('redactingLog')

/**
 * Vinyl logs request URLs and payloads in plain debug lines. Masking them where the logger
 * dispatches keeps secrets out of every sink: console, history, the log stream and the error
 * reporter's history upload.
 */
export function installLogRedaction(): void {
    const logger = loggerRef.value
    const original = Reflect.get(logger, 'log') as typeof logger.log & { [LOG_REDACTING]?: true }
    if (original[LOG_REDACTING]) return
    const redacting = function log(
        this: typeof logger,
        ...[target, level, ...messages]: Parameters<typeof logger.log>
    ) {
        // The logger drops a line below its level; masking one first copies every message for nothing.
        if (level === LogLevel.NONE || level < this.logLevel) return
        const masked = messages.map((message) =>
            typeof message === 'string' ? redactText(message) : redactJson(toJson(message))
        )
        original.call(this, target, level, ...masked)
    }
    Object.assign(redacting, { [LOG_REDACTING]: true })
    logger.log = redacting
}
